import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { dreService, DreData, DreSecao } from '../services/dre';
import { MonthPicker } from '../components/ui/MonthPicker';
import { formatCurrency, formatPercent } from '../utils/format';

export const DRE: React.FC = () => {
  const [data, setData] = useState<DreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(new Date().toISOString().substring(0, 7));
  const [anoSelecionado, mesSelecionado] = periodo.split('-');

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await dreService.get(mesSelecionado, anoSelecionado);
      setData(result);
    } catch (error) {
      console.error('Erro ao buscar DRE:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const exibicaoPeriodo = mesSelecionado === '00'
    ? `Consolidado ${anoSelecionado}`
    : `Demonstrativo de Resultado`;

  const totalReceitas             = data?.receitas.total ?? 0;
  const totalCustos               = data?.custos.total ?? 0;
  const totalDespesasOperacionais = data?.despesas_operacionais.total ?? 0;
  const totalDespesasFinanceiras  = data?.despesas_financeiras.total ?? 0;
  const margemContribuicao        = data?.margem_contribuicao ?? 0;
  const resultadoLiquido          = data?.resultadoLiquido ?? 0;
  const margemLiquida             = totalReceitas > 0 ? (resultadoLiquido / totalReceitas) * 100 : 0;

  const kpis = [
    { label: 'Receita Bruta',  value: totalReceitas, icon: 'trending_up', color: 'text-emerald-400' },
    { label: 'Total Despesas', value: totalCustos + totalDespesasOperacionais + totalDespesasFinanceiras, icon: 'shopping_cart', color: 'text-rose-400' },
    { label: 'Lucro Líquido',  value: resultadoLiquido, icon: 'stars', color: 'text-primary' },
  ];

  return (
    <Layout pageTitle="DRE" breadcrumb="Financeiro">
      <div className="space-y-8 animate-fade-in-up">
        {/* Header */}
        <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white">{exibicaoPeriodo}</h2>
            <p className="text-slate-500 mt-1">
              Visão analítica de performance e saúde financeira.
            </p>
            <p className="text-[11px] text-slate-600 mt-1 italic">
              ⓘ Regime de competência — inclui lançamentos pendentes/vencidos. Cancelados são excluídos.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <MonthPicker
              value={periodo}
              onChange={(val) => setPeriodo(val)}
            />
          </div>
        </section>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {kpis.map((kpi, idx) => (
            <div key={idx} className="glass-panel p-6 rounded-2xl border-slate-800/50 hover:bg-slate-800/20 transition-all group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-slate-800/80 rounded-xl border border-slate-700/50">
                  <span className={`material-symbols-outlined ${kpi.color}`}>{kpi.icon}</span>
                </div>
              </div>
              <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">{kpi.label}</p>
              <h3 className="text-2xl font-black text-white mt-1">{formatCurrency(kpi.value)}</h3>
            </div>
          ))}
        </div>

        {/* DRE Table */}
        <section className="glass-panel rounded-2xl overflow-hidden border-slate-800/50">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead>
                <tr className="border-b border-slate-800/80 bg-slate-800/30">
                  <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider w-[50%]">Estrutura de Contas</th>
                  <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Valor Período</th>
                  <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider text-right w-48">Representatividade (%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/30">
                {loading || !data ? (
                  <tr>
                    <td colSpan={3} className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em] animate-pulse">
                          Calculando Demonstrativo...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    <DreSectionRow
                      label="Receita Bruta Operacional"
                      value={totalReceitas}
                      total={totalReceitas}
                      icon="add_circle"
                      iconColor="text-emerald-400"
                      barColor="bg-emerald-500/50"
                    />
                    {renderDetalhes(data.receitas, totalReceitas, 'emerald')}

                    <DreSectionRow
                      label="(-) Custos Variáveis"
                      value={-totalCustos}
                      total={totalReceitas}
                      icon="remove_circle"
                      iconColor="text-rose-400"
                      barColor="bg-rose-500/40"
                    />
                    {renderDetalhes(data.custos, totalReceitas, 'rose')}

                    <DreSubtotalRow
                      label="= Margem de Contribuição"
                      value={margemContribuicao}
                      total={totalReceitas}
                      positive={margemContribuicao >= 0}
                    />

                    <DreSectionRow
                      label="(-) Despesas Operacionais"
                      value={-totalDespesasOperacionais}
                      total={totalReceitas}
                      icon="remove_circle"
                      iconColor="text-rose-400"
                      barColor="bg-rose-500/40"
                    />
                    {renderDetalhes(data.despesas_operacionais, totalReceitas, 'rose')}

                    <DreSectionRow
                      label="(-) Despesas Financeiras"
                      value={-totalDespesasFinanceiras}
                      total={totalReceitas}
                      icon="remove_circle"
                      iconColor="text-rose-400"
                      barColor="bg-rose-500/40"
                    />
                    {renderDetalhes(data.despesas_financeiras, totalReceitas, 'rose')}

                    {/* Resultado Líquido */}
                    <tr className="bg-slate-800/40 border-t-2 border-slate-700/50">
                      <td className="px-5 py-8 flex items-center gap-4">
                        <div className={`p-3 rounded-2xl shadow-lg ${resultadoLiquido >= 0 ? 'bg-emerald-500 shadow-emerald-500/30' : 'bg-rose-500 shadow-rose-500/30'}`}>
                          <span className="material-symbols-outlined text-white text-[28px]">stars</span>
                        </div>
                        <div>
                          <span className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-1 block">Resultado Final</span>
                          <span className="text-xl font-black text-white">{resultadoLiquido >= 0 ? 'LUCRO LÍQUIDO' : 'PREJUÍZO LÍQUIDO'}</span>
                        </div>
                      </td>
                      <td className={`px-5 py-8 text-3xl font-black text-right ${resultadoLiquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(resultadoLiquido)}
                      </td>
                      <td className="px-5 py-8 text-right">
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-lg font-black ${resultadoLiquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {formatPercent(margemLiquida)}
                          </span>
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Margem Líquida</span>
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Layout>
  );
};

/* ============================================================================
 * Subcomponentes
 * ============================================================================ */

interface SectionRowProps {
  label: string;
  value: number;
  total: number;
  icon: string;
  iconColor: string;
  barColor: string;
}

const DreSectionRow: React.FC<SectionRowProps> = ({ label, value, total, icon, iconColor, barColor }) => {
  if (value === 0) return null;
  const pct = total > 0 ? (Math.abs(value) / total) * 100 : 0;
  const isNegative = value < 0;
  return (
    <tr className="bg-slate-800/10 hover:bg-slate-800/20 transition-colors">
      <td className="px-5 py-5 flex items-center gap-3">
        <span className={`material-symbols-outlined ${iconColor} text-[20px]`}>{icon}</span>
        <span className="text-sm font-black uppercase tracking-widest text-slate-200">{label}</span>
      </td>
      <td className={`px-5 py-5 text-base font-black text-right ${isNegative ? 'text-rose-400' : 'text-white'}`}>
        {isNegative ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value)}
      </td>
      <td className="px-5 py-5 text-right">
        <div className="flex items-center justify-end gap-3 font-bold text-slate-500">
          <span className="text-xs">{formatPercent(pct)}</span>
          <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
          </div>
        </div>
      </td>
    </tr>
  );
};

interface SubtotalRowProps {
  label: string;
  value: number;
  total: number;
  positive: boolean;
}

const DreSubtotalRow: React.FC<SubtotalRowProps> = ({ label, value, total, positive }) => {
  const pct = total > 0 ? (Math.abs(value) / total) * 100 : 0;
  return (
    <tr className="bg-slate-800/30 border-y border-slate-700/40">
      <td className="px-5 py-4 pl-8 flex items-center gap-2">
        <span className="material-symbols-outlined text-slate-500 text-[18px]">summarize</span>
        <span className="text-sm font-black uppercase tracking-widest text-slate-300">{label}</span>
      </td>
      <td className={`px-5 py-4 text-base font-black text-right ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
        {formatCurrency(value)}
      </td>
      <td className="px-5 py-4 text-right">
        <div className="flex items-center justify-end gap-3 font-bold text-slate-500">
          <span className="text-xs">{formatPercent(pct)}</span>
          <div className="w-24 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full ${positive ? 'bg-emerald-500/40' : 'bg-rose-500/40'}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
          </div>
        </div>
      </td>
    </tr>
  );
};

function renderDetalhes(secao: DreSecao, totalReceitas: number, color: 'emerald' | 'rose') {
  const isNegative = color === 'rose';
  return secao.detalhes.map((cat) => {
    const pct = totalReceitas > 0 ? (cat.valor / totalReceitas) * 100 : 0;
    return (
      <tr key={`det-${color}-${cat.id}-${cat.nome}`} className="hover:bg-slate-800/30 transition-colors">
        <td className="px-5 py-3 pl-12 flex items-center gap-3">
          <span className={`w-1.5 h-1.5 rounded-full ${color === 'emerald' ? 'bg-emerald-500/40' : 'bg-rose-500/40'}`}></span>
          <span className="text-sm text-slate-400">{cat.nome}</span>
        </td>
        <td className="px-5 py-3 text-sm font-bold text-right text-slate-300">
          {isNegative ? `(${formatCurrency(cat.valor)})` : formatCurrency(cat.valor)}
        </td>
        <td className="px-5 py-3 text-right">
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-slate-500 font-bold">{formatPercent(pct)}</span>
            <div className="w-24 h-1 bg-slate-800 rounded-full overflow-hidden">
              <div className={`h-full ${color === 'emerald' ? 'bg-emerald-500/30' : 'bg-rose-500/30'}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
            </div>
          </div>
        </td>
      </tr>
    );
  });
}
